import { useRef, useState } from "react";
import { format } from "date-fns";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Chip,
  Stack,
} from "@mui/material";
import { useAxleDataUpsertLastRunTimesQuery, useAxleDataUpsertOrgsQuery, useUpsertAxleDataMutation, UpsertAxleDataMutation } from "../../../types";
import { useSnackbar } from "../../../components/Snackbar/SnackbarContext";
import { ProfileConflictsAudit } from "./ProfileConflictsAudit";

// Keep in sync with AXLE_DATA_UPSERT_MAX_ORGS in apps/server/src/app/services/AxleDataUpsert.ts
const MAX_ORGS_PER_UPSERT = 10;
const ALL_TERRITORIES_VALUE = "__all__";

type UpsertOrg = {
  id: string;
  name: string;
  territoryName: string | null;
};

type UpsertResult = UpsertAxleDataMutation["upsertAxleData"];

type SubmittedUpsertRequest = {
  dryRun: boolean;
  missingPersonDocumentsOnly: boolean;
  orgIds: string[];
};

const sortOrgsByName = (orgs: UpsertOrg[]) => [...orgs].sort((a, b) => a.name.localeCompare(b.name));

const matchesTerritory = (org: UpsertOrg, territoryFilter: string) =>
  territoryFilter === ALL_TERRITORIES_VALUE || org.territoryName === territoryFilter;

const formatLastRunAt = (lastRunAt: Date) => format(lastRunAt, "MMM d, yyyy h:mm a");

const orgIdsBlockedByProfileConflicts = (conflicts: UpsertResult["profileConflicts"]): Set<string> => {
  const orgIds = new Set<string>();
  for (const conflict of conflicts) {
    if (conflict.orgId) {
      orgIds.add(conflict.orgId);
    }
    for (const membership of conflict.memberships) {
      if (membership.orgId && membership.profileKind !== "existing_owner") {
        orgIds.add(membership.orgId);
      }
    }
  }
  return orgIds;
};

export const AxleDataUpsertPage = () => {
  const [selectedOrgIds, setSelectedOrgIds] = useState<string[]>([]);
  const [completedOrgIds, setCompletedOrgIds] = useState<string[]>([]);
  const [territoryFilter, setTerritoryFilter] = useState(ALL_TERRITORIES_VALUE);
  const [dryRun, setDryRun] = useState(true);
  const [missingPersonDocumentsOnly, setMissingPersonDocumentsOnly] = useState(true);
  const [result, setResult] = useState<UpsertResult | null>(null);
  const [resultDryRun, setResultDryRun] = useState(true);
  const submittedRequestRef = useRef<SubmittedUpsertRequest | null>(null);

  const { data: orgsData, loading: orgsLoading } = useAxleDataUpsertOrgsQuery();
  const { data: lastRunData, refetch: refetchLastRunTimes } = useAxleDataUpsertLastRunTimesQuery({
    skip: selectedOrgIds.length === 0,
    variables: { orgIds: selectedOrgIds },
  });
  const { setMessage } = useSnackbar();

  const allOrgs = orgsData?.orgs ?? [];

  const territoryNames = new Set<string>();
  for (const org of allOrgs) {
    if (org.territoryName) {
      territoryNames.add(org.territoryName);
    }
  }
  const territoryOptions = [...territoryNames].sort((a, b) => a.localeCompare(b));

  const filteredOrgs = sortOrgsByName(allOrgs.filter(org => matchesTerritory(org, territoryFilter)));
  const remainingOrgIds = filteredOrgs.filter(org => !completedOrgIds.includes(org.id)).map(org => org.id);

  const orgsById = new Map(allOrgs.map(org => [org.id, org]));
  const selectedOrgs = sortOrgsByName(
    selectedOrgIds
      .map(id => orgsById.get(id))
      .filter((org): org is UpsertOrg => org !== undefined)
  );

  const territoryFilterLabel =
    territoryFilter === ALL_TERRITORIES_VALUE ? "All territories" : territoryFilter;

  const lastRunByOrgId = new Map<string, Date>();
  for (const entry of lastRunData?.axleDataUpsertLastRunTimes ?? []) {
    if (entry.lastRunAt) {
      lastRunByOrgId.set(entry.orgId, new Date(entry.lastRunAt));
    }
  }

  const removeSelectedOrg = (orgId: string) => {
    setSelectedOrgIds(prev => prev.filter(id => id !== orgId));
    setResult(null);
  };

  const [upsertAxleData, { loading: mutationLoading }] = useUpsertAxleDataMutation({
    onCompleted: data => {
      const submitted = submittedRequestRef.current;
      const wasDryRun = submitted?.dryRun ?? true;
      const submittedOrgIds = submitted?.orgIds ?? [];

      setResult(data.upsertAxleData);
      setResultDryRun(wasDryRun);

      const conflictCount = data.upsertAxleData.profileConflicts.length;
      if (conflictCount > 0) {
        setMessage(wasDryRun ? "warning" : "error", data.upsertAxleData.message);
      } else {
        setMessage("success", data.upsertAxleData.message);
      }

      if (!wasDryRun) {
        const blockedOrgIds = orgIdsBlockedByProfileConflicts(data.upsertAxleData.profileConflicts);
        const succeededOrgIds = submittedOrgIds.filter(orgId => !blockedOrgIds.has(orgId));
        if (succeededOrgIds.length > 0) {
          setCompletedOrgIds(prev => Array.from(new Set([...prev, ...succeededOrgIds])));
          void refetchLastRunTimes();
        }
      }
    },
    onError: e => {
      setMessage("error", e.message);
      setResult(null);
    },
  });

  const selectNextBatch = (poolOrgIds: string[]) => {
    const nextBatch = poolOrgIds.filter(id => !completedOrgIds.includes(id)).slice(0, MAX_ORGS_PER_UPSERT);

    if (nextBatch.length === 0) {
      setMessage("info", "No remaining organizations in this selection");
      return;
    }

    setSelectedOrgIds(nextBatch);
    setResult(null);
  };

  const handleSubmit = () => {
    if (selectedOrgIds.length === 0) {
      setMessage("error", "Please select at least one organization");
      return;
    }

    if (selectedOrgIds.length > MAX_ORGS_PER_UPSERT) {
      setMessage("error", `Please select no more than ${MAX_ORGS_PER_UPSERT} organizations per batch`);
      return;
    }

    setResult(null);
    submittedRequestRef.current = { dryRun, missingPersonDocumentsOnly, orgIds: selectedOrgIds };

    upsertAxleData({
      variables: {
        input: {
          dryRun,
          orgIds: selectedOrgIds,
          missingPersonDocumentsOnly,
        },
      },
    });
  };

  if (orgsLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200 }}>
      <Typography variant="h4" gutterBottom>
        Axle Data Upsert
      </Typography>

                  <Alert severity="info" sx={{ mb: 3 }}>
        Upserts run synchronously and typically take about 30–40 seconds per batch of 10 organizations. Run live upserts in
        batches, then use Select next 10 to continue. By default, only memberships missing an Axle person document ID (TWRN)
        are upserted — uncheck &quot;Missing TWRNs only&quot; to re-upsert all profiles. Dry run does not write to Axle or
        Nutrition; it reads existing Axle person documents to predict document-ID collisions.
      </Alert>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(0, 1fr)" },
          gap: 3,
          alignItems: "start",
        }}
      >
        <Stack spacing={3}>
          <Paper sx={{ p: 3 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="territory-filter-label">Territory</InputLabel>
              <Select
                labelId="territory-filter-label"
                value={territoryFilter}
                onChange={e => {
                  setTerritoryFilter(e.target.value);
                  setSelectedOrgIds([]);
                  setResult(null);
                }}
                label="Territory"
                renderValue={() => territoryFilterLabel}
              >
                <MenuItem value={ALL_TERRITORIES_VALUE}>All territories</MenuItem>
                {territoryOptions.map(name => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="org-select-label">Organizations</InputLabel>
              <Select
                labelId="org-select-label"
                multiple
                value={selectedOrgIds}
                onChange={e => setSelectedOrgIds(typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value)}
                label="Organizations"
                renderValue={selected => `${selected.length} organization${selected.length !== 1 ? "s" : ""} selected`}
              >
                {filteredOrgs.map(org => (
                  <MenuItem key={org.id} value={org.id}>
                    <Checkbox checked={selectedOrgIds.indexOf(org.id) > -1} />
                    {org.name}
                    {org.territoryName ? ` (${org.territoryName})` : ""}
                    {completedOrgIds.includes(org.id) ? " — completed" : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}>
              <Chip label={`${remainingOrgIds.length} remaining in filter`} size="small" />
              <Chip label={`${completedOrgIds.length} completed this session`} size="small" color="success" variant="outlined" />
            </Stack>

            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}>
              <Button variant="outlined" onClick={() => selectNextBatch(filteredOrgs.map(org => org.id))} disabled={remainingOrgIds.length === 0}>
                Select next 10
              </Button>

              {completedOrgIds.length > 0 && (
                <Button
                  variant="text"
                  onClick={() => {
                    setCompletedOrgIds([]);
                    setResult(null);
                  }}
                >
                  Reset completed
                </Button>
              )}
            </Stack>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select up to {MAX_ORGS_PER_UPSERT} organizations per batch
            </Typography>

            <Stack spacing={0}>
              <FormControlLabel
                control={<Checkbox checked={dryRun} onChange={e => setDryRun(e.target.checked)} />}
                label="Dry Run (preview changes without applying)"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={missingPersonDocumentsOnly}
                    onChange={e => setMissingPersonDocumentsOnly(e.target.checked)}
                  />
                }
                label="Missing TWRNs only (skip already-linked profiles)"
              />
            </Stack>

            <Box sx={{ mt: 2 }}>
              <Button variant="contained" onClick={handleSubmit} disabled={mutationLoading || selectedOrgIds.length === 0}>
                {mutationLoading ? <CircularProgress size={24} /> : dryRun ? "Preview Upsert" : "Run Upsert"}
              </Button>
            </Box>
          </Paper>

          {result && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h3" gutterBottom>
                Results
              </Typography>

              <Alert
                severity={
                  result.profileConflicts.length > 0
                    ? resultDryRun
                      ? "warning"
                      : "error"
                    : result.success
                      ? "success"
                      : "error"
                }
                sx={{ mb: 2 }}
              >
                {result.message}
              </Alert>

              {!resultDryRun && remainingOrgIds.length > 0 && completedOrgIds.length > 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Batch complete. {remainingOrgIds.length} organization{remainingOrgIds.length !== 1 ? "s" : ""} remaining in
                  the current filter.
                </Alert>
              )}

              <ProfileConflictsAudit conflicts={result.profileConflicts} />

              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Nutrition Orgs
                  </Typography>
                  <Typography variant="h3">{result.summary.totalNutritionOrgs}</Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Already Synced
                  </Typography>
                  <Typography variant="h3">{result.summary.alreadySynced}</Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Matched and Synced
                  </Typography>
                  <Typography variant="h3">{result.summary.matchedAndSynced}</Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Created New Orgs
                  </Typography>
                  <Typography variant="h3" sx={{ color: "success.main" }}>
                    {result.summary.created}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    No Matches
                  </Typography>
                  <Typography variant="h3">{result.summary.noMatches}</Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Unexpected Partial Matches
                  </Typography>
                  <Typography variant="h3">{result.summary.unexpectedPartialMatches}</Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Orgs Processed
                  </Typography>
                  <Typography variant="h3">{result.summary.orgsProcessed}</Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Athlete Profiles
                  </Typography>
                  <Typography variant="h3">{result.summary.totalAthleteProfiles}</Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Non-Athlete Profiles
                  </Typography>
                  <Typography variant="h3">{result.summary.totalNonAthleteProfiles}</Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Skipped Already Linked
                  </Typography>
                  <Typography variant="h3">{result.summary.skippedAlreadyLinkedProfiles}</Typography>
                </Box>

                <Box sx={{ gridColumn: "1 / -1" }}>
                  <Typography variant="body2" color="text.secondary">
                    Total Profiles
                  </Typography>
                  <Typography variant="h3" sx={{ fontSize: "2rem" }}>
                    {result.summary.totalProfiles}
                  </Typography>
                </Box>
              </Box>

              {!resultDryRun && remainingOrgIds.length > 0 && completedOrgIds.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Button variant="contained" onClick={() => selectNextBatch(filteredOrgs.map(org => org.id))}>
                    Select next 10
                  </Button>
                </Box>
              )}
            </Paper>
          )}
        </Stack>

        <Paper sx={{ p: 3 }}>
            <Typography variant="h3" gutterBottom>
              Selected organizations
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Territory filter: {territoryFilterLabel}
            </Typography>

            {selectedOrgs.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No organizations selected. Use the dropdown or Select next 10 to choose a batch.
              </Typography>
            ) : (
              <Stack spacing={1}>
                {selectedOrgs.map((org, index) => (
                  <Box
                    key={org.id}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 1,
                      py: 0.5,
                      borderBottom: index < selectedOrgs.length - 1 ? 1 : 0,
                      borderColor: "divider",
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body1">{org.name}</Typography>
                      {org.territoryName && (
                        <Typography variant="body2" color="text.secondary">
                          {org.territoryName}
                        </Typography>
                      )}
                    </Box>
                    <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
                      {lastRunByOrgId.has(org.id) && (
                        <Chip
                          label={`Last run: ${formatLastRunAt(lastRunByOrgId.get(org.id)!)}`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                      {completedOrgIds.includes(org.id) && <Chip label="Completed" size="small" color="success" />}
                      <Button size="small" onClick={() => removeSelectedOrg(org.id)}>
                        Remove
                      </Button>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
          </Paper>
      </Box>
    </Box>
  );
};
